import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../theme/colors';
import { spacing } from '../theme/spacing';
import { typography } from '../theme/typography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';

export interface StatChipProps {
  icon: string;
  value: number;
  label: string;
}

/** Small icon + bold number + muted label chip — extracted from
 * ProfileCompletionWidget.tsx so it can be reused verbatim by
 * PlatformPerformanceCard.tsx (and any future card) instead of each
 * duplicating the same styling independently. */
export const StatChip: React.FC<StatChipProps> = ({ icon, value, label }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const typo = useTypography();

  return (
    <View style={styles.statChip}>
      <View style={[styles.statChipTop, { flexDirection: rtl.rowDirection }]}>
        <Ionicons name={icon as any} size={16} color={colors.primary} />
        <Text style={[styles.statChipValue, { fontFamily: typo.fontFamily.bold }]}>{value.toLocaleString()}</Text>
      </View>
      <Text style={[styles.statChipLabel, { textAlign: rtl.textStart, fontFamily: typo.fontFamily.primary }]} numberOfLines={1}>
        {label}
      </Text>
    </View>
  );
};

export const statsStripStyle = {
  flexDirection: 'row' as const,
  justifyContent: 'space-between' as const,
  gap: spacing.xs,
  marginBottom: spacing.sm,
};

const getStyles = () => StyleSheet.create({
  statChip: {
    flex: 1,
    backgroundColor: colors.background.secondary,
    borderRadius: spacing.borderRadius.sm,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
  },
  statChipTop: {
    alignItems: 'center',
    gap: 4,
    marginBottom: 2,
  },
  statChipValue: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  statChipLabel: {
    fontSize: 10,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
});
